import React from "react";
import PropTypes from "prop-types";
import { Box, Avatar, Typography, Button, IconButton } from "@mui/material";
import {
    EditOutlined,
    ShareOutlined,
    PersonAddAlt1,
    WarningAmberOutlined,
    HowToRegOutlined,
    ScheduleSendOutlined,
    Check,
    Close,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import {
    pickAvatarUpdatedAtMs,
    resolveAvatarSrc,
} from "../../utils/avatarUrl";
import { colors } from "../../theme/colors";
import TooltipComponent from "../common/Tooltip";
import { toast } from "../../universal/toast";
import utils from "../../universal/utils";
import { ProfileHeaderSkeleton } from "./profileSkeletons";
import { buildProfileExternalLinks } from "./externalProfileLinks";
import { MembershipBadge, resolveMembershipDisplay } from "./profileIdentityBadges";

const MotionBox = motion(Box);

const basePath = utils.getEdgeBasePath();

const STEAM_ICON_URL =
    "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/steam-icon-14889.png";
const FACEIT_ICON_URL =
    "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/faceit-seeklogo.svg";

async function copyTextToClipboard(value) {
    const text = String(value || "").trim();
    if (!text) return false;

    try {
        if (
            typeof navigator !== "undefined" &&
            typeof navigator.clipboard?.writeText === "function"
        ) {
            try {
                await navigator.clipboard.writeText(text);
                return true;
            } catch (_) {
                // Fall through for browsers that block the Clipboard API.
            }
        }

        if (typeof document === "undefined" || !document.body) return false;

        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.setAttribute("aria-hidden", "true");
        textarea.tabIndex = -1;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        try {
            textarea.focus({ preventScroll: true });
            textarea.select();
            textarea.setSelectionRange(0, textarea.value.length);
            return document.execCommand("copy");
        } finally {
            textarea.remove();
        }
    } catch (_) {
        return false;
    }
}

const FLAG_BADGE_STYLES = {
    position: "absolute",
    right: "6px",
    bottom: "6px",
    width: "1.35rem",
    height: "1.35rem",
    borderRadius: "50%",
    border: `2px solid ${alpha(colors.background.surface, 0.4)}`,
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.45), 0 0 0 2px rgba(91, 33, 230, 0.25)",
    backgroundColor: alpha(colors.background.surface, 0.8),
    objectFit: "cover",
};


const PROFILE_CONTENT_WIDTH = "266.5px";

const CARD_SX = {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.85rem",
    p: "1.15rem 1rem 1.1rem",
    borderRadius: "14px",
    overflow: "hidden",
    isolation: "isolate",
    background: `
        linear-gradient(
            180deg,
            rgba(12,12,14,0.96) 0%,
            rgba(8,8,10,0.985) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    backdropFilter: "blur(18px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.05),
        inset 0 -40px 80px rgba(246,185,76,0.03),
        0 20px 50px rgba(0,0,0,0.4)
    `,
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "42%",
        height: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(246,185,76,0.85),
                transparent
            )
        `,
        boxShadow: "0 0 14px rgba(246,185,76,0.35)",
        pointerEvents: "none",
        zIndex: 2,
    },
};

const IDENTITY_CARD_SX = {
    position: "relative",
    width: PROFILE_CONTENT_WIDTH,
    height: "350px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1.15rem",
    p: "1.5rem 1rem",
    overflow: "hidden",
    borderRadius: "24px",
    border: "1px solid rgba(232,163,61,0.12)",
    background: "#070708",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.04),
        inset 0 -1px 0 rgba(255,255,255,0.02),
        0 5px 18px rgba(0,0,0,0.16)
    `,
    isolation: "isolate",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: `
            linear-gradient(
                180deg,
                rgba(255,255,255,0.025),
                transparent 20%
            )
        `,
        zIndex: 0,
    },
    "& > *": {
        position: "relative",
        zIndex: 1,
    },
};

function ProfileExternalLinkButton({
    href,
    tooltip,
    iconSrc,
    iconBackground,
    iconSize,
}) {
    return (
        <TooltipComponent title={tooltip} placement="top">
            <Box
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={tooltip}
                sx={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "2.5rem",
                    height: "2.5rem",
                    flex: "0 0 2.5rem",
                    p: 0,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    borderRadius: "12px",
                    border: `1px solid ${alpha(colors.primary.main, 0.14)}`,
                    background: `
                        linear-gradient(
                            145deg,
                            rgba(16,16,18,0.96) 0%,
                            rgba(9,9,11,0.99) 52%,
                            rgba(6,6,8,1) 100%
                        )
                    `,
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.045),
                        inset 0 -24px 48px rgba(0,0,0,0.24),
                        0 10px 28px rgba(0,0,0,0.2)
                    `,
                    color: colors.text.primary,
                    textDecoration: "none",
                    transition:
                        "transform 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        borderColor: alpha(colors.accent.main, 0.34),
                        background: `
                            linear-gradient(
                                145deg,
                                ${alpha(colors.primary.main, 0.105)} 0%,
                                rgba(13,13,15,0.98) 52%,
                                rgba(7,7,9,1) 100%
                            )
                        `,
                        boxShadow: `
                            inset 0 1px 0 rgba(255,255,255,0.065),
                            inset 0 0 22px ${alpha(colors.primary.main, 0.035)},
                            0 12px 30px rgba(0,0,0,0.28),
                            0 0 18px ${alpha(colors.primary.main, 0.08)}
                        `,
                        "& .external-profile-icon": {
                            borderColor: alpha(colors.accent.main, 0.32),
                            boxShadow: `
                                inset 0 1px 0 rgba(255,255,255,0.08),
                                0 5px 14px rgba(0,0,0,0.38),
                                0 0 12px ${alpha(colors.primary.main, 0.12)}
                            `,
                            transform: "scale(1.04)",
                        },
                    },
                    "&:focus-visible": {
                        outline: `2px solid ${alpha(colors.primary.main, 0.72)}`,
                        outlineOffset: "2px",
                    },
                }}
            >
                <Box
                    className="external-profile-icon"
                    sx={{
                        width: "1.8rem",
                        height: "1.8rem",
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        borderRadius: "8px",
                        backgroundColor: iconBackground,
                        border: "1px solid rgba(255,255,255,0.09)",
                        boxShadow: `
                            inset 0 1px 0 rgba(255,255,255,0.055),
                            0 4px 10px rgba(0,0,0,0.3)
                        `,
                        transition:
                            "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
                    }}
                >
                    <Box
                        component="img"
                        src={iconSrc}
                        alt=""
                        aria-hidden="true"
                        sx={{
                            width: iconSize,
                            height: iconSize,
                            objectFit: "contain",
                        }}
                    />
                </Box>
            </Box>
        </TooltipComponent>
    );
}

ProfileExternalLinkButton.propTypes = {
    href: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
    iconSrc: PropTypes.string.isRequired,
    iconBackground: PropTypes.string.isRequired,
    iconSize: PropTypes.string.isRequired,
};

function resolveCountryLabel(name) {
    const trimmed = String(name || "").trim();
    if (!trimmed || /^unknown$/i.test(trimmed)) {
        return "";
    }
    return trimmed;
}

function ProfileMetaChips({
    flagImageUrl,
    countryDisplayName,
    joinedDateDisplay,
    steamProfileUrl,
    faceitProfileUrl,
}) {
    const countryLabel = resolveCountryLabel(countryDisplayName);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "0.65rem",
                width: "100%",
                boxSizing: "border-box",
                mt: "1rem",
                px: "0.35rem",
            }}
        >
            {flagImageUrl || countryLabel ? (
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.4rem",
                        minWidth: 0,
                    }}
                >
                    {flagImageUrl ? (
                        <Box
                            component="img"
                            src={flagImageUrl}
                            alt=""
                            aria-hidden="true"
                            sx={{
                                width: "1.4rem",
                                height: "1.1rem",
                                borderRadius: "4px",
                                objectFit: "cover",
                                flexShrink: 0,
                            }}
                        />
                    ) : null}
                    {countryLabel ? (
                        <Typography
                            sx={{
                                fontSize: "0.875rem",
                                color: colors.text.primary,
                                fontWeight: 500,
                                lineHeight: 1.3,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {countryLabel}
                        </Typography>
                    ) : null}
                </Box>
            ) : null}

            <Box
                sx={{
                    display: "inline-flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: "0.25rem",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.86rem",
                        lineHeight: 1.35,
                        color: colors.text.secondary,
                        fontWeight: 500,
                    }}
                >
                    Member since
                </Typography>
                <Typography
                    sx={{
                        fontSize: "0.86rem",
                        lineHeight: 1.35,
                        color: colors.text.primary,
                        fontWeight: 600,
                    }}
                >
                    {joinedDateDisplay}
                </Typography>
            </Box>

            {steamProfileUrl || faceitProfileUrl ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.65rem",
                        mt: "0.3rem",
                    }}
                >
                    {steamProfileUrl ? (
                        <ProfileExternalLinkButton
                            href={steamProfileUrl}
                            tooltip="Steam"
                            iconSrc={STEAM_ICON_URL}
                            iconBackground="#171A21"
                            iconSize="1.72rem"
                        />
                    ) : null}
                    {faceitProfileUrl ? (
                        <ProfileExternalLinkButton
                            href={faceitProfileUrl}
                            tooltip="FACEIT"
                            iconSrc={FACEIT_ICON_URL}
                            iconBackground="#F5F6F7"
                            iconSize="1.08rem"
                        />
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
}

ProfileMetaChips.propTypes = {
    flagImageUrl: PropTypes.string,
    countryDisplayName: PropTypes.string,
    joinedDateDisplay: PropTypes.string.isRequired,
    steamProfileUrl: PropTypes.string,
    faceitProfileUrl: PropTypes.string,
};

function ProfileCardDivider() {
    return (
        <Box
            aria-hidden="true"
            sx={{
                position: "relative",
                width: "100%",
                height: "1px",
                mt: "0.85rem",
                mb: "0.2rem",
                background: `
                    linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255,255,255,0.06) 12%,
                        rgba(255,255,255,0.1) 50%,
                        rgba(255,255,255,0.06) 88%,
                        transparent 100%
                    )
                `,
                "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "28%",
                    height: "1px",
                    background: `
                        linear-gradient(
                            90deg,
                            transparent,
                            rgba(246,185,76,0.55),
                            transparent
                        )
                    `,
                    boxShadow: "0 0 10px rgba(246,185,76,0.28)",
                },
            }}
        />
    );
}


function ProfileDesktopActionButton({
    label,
    onClick,
    disabled = false,
    tone = "default",
    startIcon = null,
    fullWidth = false,
}) {
    const isPrimary = tone === "primary";
    const isSuccess = tone === "success";
    const isPending = tone === "pending";
    const isReport = tone === "report";
    const isFilled = isPrimary || isSuccess;
    const actionClip = "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)";

    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            startIcon={startIcon}
            fullWidth={fullWidth}
            sx={{
                textTransform: "none",
                fontWeight: isReport || isPending ? 600 : 700,
                fontSize: "0.78rem",
                letterSpacing: "0.03em",
                minHeight: 36,
                px: isReport ? "0.8rem" : "0.9rem",
                borderRadius: "8px",
                clipPath: isFilled || isPending ? actionClip : "none",
                transition:
                    "background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease, filter 160ms ease",
                color: isPrimary
                    ? colors.text.contrast
                    : isSuccess
                      ? alpha("#BBF7D0", 0.94)
                      : isPending
                        ? alpha(colors.accent.soft, 0.88)
                        : alpha("#fff", 0.62),
                background: isPrimary
                    ? colors.gradient.accent
                    : isSuccess
                      ? `
                            linear-gradient(
                                180deg,
                                ${alpha(colors.state.success.main, 0.14)} 0%,
                                ${alpha(colors.state.success.main, 0.06)} 100%
                            )
                          `
                      : isPending
                        ? `
                            linear-gradient(
                                135deg,
                                rgba(232,163,61,0.12) 0%,
                                rgba(255,255,255,0.03) 55%,
                                rgba(0,0,0,0.2) 100%
                            )
                          `
                        : `
                            linear-gradient(
                                180deg,
                                rgba(255,255,255,0.045) 0%,
                                rgba(255,255,255,0.02) 100%
                            )
                          `,
                border: isPrimary
                    ? "1px solid rgba(246,199,107,0.4)"
                    : isSuccess
                      ? `1px solid ${alpha(colors.state.success.main, 0.22)}`
                      : isPending
                        ? `1px solid ${alpha(colors.primary.main, 0.28)}`
                        : "1px solid rgba(255,255,255,0.1)",
                boxShadow: isPrimary
                    ? "0 6px 16px rgba(232,163,61,0.16)"
                    : isSuccess
                      ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                      : isPending
                        ? `
                            inset 0 1px 0 rgba(255,255,255,0.06),
                            0 4px 12px rgba(0,0,0,0.22)
                          `
                        : `
                            inset 0 1px 0 rgba(255,255,255,0.05),
                            0 4px 12px rgba(0,0,0,0.18)
                          `,
                backdropFilter: isReport || isPending ? "blur(8px)" : "none",
                "& .MuiButton-startIcon": {
                    marginRight: "0.4rem",
                    color: isPending
                        ? alpha(colors.accent.main, 0.9)
                        : isReport
                          ? alpha("#fff", 0.5)
                          : "inherit",
                },
                "&:hover": isReport
                    ? {
                          color: "#FCA5A5",
                          borderColor: alpha(colors.state.danger.main, 0.45),
                          background: `
                              linear-gradient(
                                  180deg,
                                  ${alpha(colors.state.danger.main, 0.14)} 0%,
                                  ${alpha(colors.state.danger.main, 0.08)} 100%
                              )
                          `,
                          boxShadow: `
                              inset 0 1px 0 rgba(255,255,255,0.06),
                              0 6px 16px ${alpha(colors.state.danger.main, 0.12)}
                          `,
                          "& .MuiButton-startIcon": {
                              color: "#FCA5A5",
                          },
                      }
                    : isPrimary
                      ? {
                            filter: "brightness(1.04)",
                            boxShadow: "0 8px 18px rgba(232,163,61,0.22)",
                        }
                      : isSuccess
                        ? {
                              background: `
                                  linear-gradient(
                                      180deg,
                                      ${alpha(colors.state.success.main, 0.18)} 0%,
                                      ${alpha(colors.state.success.main, 0.08)} 100%
                                  )
                              `,
                              borderColor: alpha(colors.state.success.main, 0.3),
                          }
                        : {
                              backgroundColor: "rgba(255,255,255,0.06)",
                              borderColor: "rgba(255,255,255,0.14)",
                              color: colors.text.primary,
                          },
                "&.Mui-disabled": {
                    opacity: 1,
                    cursor: isPending || isSuccess ? "default" : "not-allowed",
                    color: isSuccess
                        ? alpha("#BBF7D0", 0.94)
                        : isPending
                          ? alpha(colors.accent.soft, 0.88)
                          : colors.text.muted,
                    background: isSuccess
                        ? `
                            linear-gradient(
                                180deg,
                                ${alpha(colors.state.success.main, 0.14)} 0%,
                                ${alpha(colors.state.success.main, 0.06)} 100%
                            )
                          `
                        : isPending
                          ? `
                              linear-gradient(
                                  135deg,
                                  rgba(232,163,61,0.12) 0%,
                                  rgba(255,255,255,0.03) 55%,
                                  rgba(0,0,0,0.2) 100%
                              )
                            `
                          : "rgba(255,255,255,0.03)",
                    borderColor: isSuccess
                        ? alpha(colors.state.success.main, 0.22)
                        : isPending
                          ? alpha(colors.primary.main, 0.28)
                          : "rgba(255,255,255,0.08)",
                    boxShadow: isPending
                        ? `
                            inset 0 1px 0 rgba(255,255,255,0.06),
                            0 4px 12px rgba(0,0,0,0.22)
                          `
                        : isSuccess
                          ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                          : "none",
                    "& .MuiButton-startIcon": {
                        color: isPending
                            ? alpha(colors.accent.main, 0.9)
                            : "inherit",
                    },
                },
            }}
        >
            {label}
        </Button>
    );
}

ProfileDesktopActionButton.propTypes = {
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    tone: PropTypes.oneOf(["default", "primary", "success", "pending", "report"]),
    startIcon: PropTypes.node,
    fullWidth: PropTypes.bool,
};

function ProfileFriendIconAction({ ariaLabel, onClick, tooltip }) {
    return (
        <TooltipComponent title={tooltip || ariaLabel} placement="top">
            <IconButton
                aria-label={ariaLabel}
                onClick={onClick}
                size="small"
                sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    color: alpha("#fff", 0.55),
                    background: "rgba(255,255,255,0.035)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    transition:
                        "background-color 160ms ease, border-color 160ms ease, color 160ms ease",
                    "&:hover": {
                        color: "#FCA5A5",
                        borderColor: alpha(colors.state.danger.main, 0.4),
                        backgroundColor: alpha(colors.state.danger.main, 0.1),
                    },
                }}
            >
                <Close sx={{ fontSize: "1.05rem" }} />
            </IconButton>
        </TooltipComponent>
    );
}

ProfileFriendIconAction.propTypes = {
    ariaLabel: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    tooltip: PropTypes.string,
};

function ProfileReportIconAction({ onClick }) {
    return (
        <TooltipComponent title="Report" placement="top">
            <IconButton
                aria-label="Report"
                onClick={onClick}
                size="small"
                sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: "8px",
                    color: alpha("#fff", 0.5),
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(255,255,255,0.045) 0%,
                            rgba(255,255,255,0.02) 100%
                        )
                    `,
                    border: "1px solid rgba(255,255,255,0.1)",
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.05),
                        0 4px 12px rgba(0,0,0,0.18)
                    `,
                    backdropFilter: "blur(8px)",
                    transition:
                        "background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease",
                    "&:hover": {
                        color: "#FCA5A5",
                        borderColor: alpha(colors.state.danger.main, 0.45),
                        backgroundColor: alpha(colors.state.danger.main, 0.1),
                        boxShadow: `0 6px 16px ${alpha(colors.state.danger.main, 0.12)}`,
                    },
                }}
            >
                <WarningAmberOutlined sx={{ fontSize: "1rem" }} />
            </IconButton>
        </TooltipComponent>
    );
}

ProfileReportIconAction.propTypes = {
    onClick: PropTypes.func,
};

function formatProfileJoinedDate(raw) {
    if (raw == null || raw === "") return "—";

    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return "—";

    const ms = n < 1e12 ? n * 1000 : n;
    const date = new Date(ms);
    if (Number.isNaN(date.getTime())) return "—";

    const formatted = date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    return formatted;
}

export default function ProfileHeader({
    profileData,
    viewerUser,
    isOwnProfile = false,
    isLoading = false,
    friendshipStatus = "none",
    onAddFriend,
    onAcceptFriend,
    onDeclineFriend,
    onCancelFriendRequest,
    onReport,
    onEditProfile,
}) {
    if (isLoading) {
        return <ProfileHeaderSkeleton />;
    }

    const avatarVersion = pickAvatarUpdatedAtMs(
        isOwnProfile ? viewerUser : null,
        profileData,
    );

    const avatarSrc = resolveAvatarSrc({
        basePath,
        picture: profileData?.picture,
        userId: profileData?.userId || profileData?._id,
        avatarUpdatedAtMs: avatarVersion,
        hasCustomAvatar: !!profileData?.avatar,
    });

    const normalizedFriendship = String(friendshipStatus || "none")
        .trim()
        .toLowerCase();
    const isFriends = normalizedFriendship === "friends";
    const isPendingOutgoing = normalizedFriendship === "pending_outgoing";
    const isPendingIncoming = normalizedFriendship === "pending_incoming";
    const joinedDateDisplay = formatProfileJoinedDate(profileData?.joined);
    const externalLinks = buildProfileExternalLinks(profileData);
    const headerMembership = resolveMembershipDisplay(profileData);
    const handleShareProfile = async () => {
        if (typeof window === "undefined") return;

        const profileId = profileData?._id;
        const pathname = profileId
            ? `/profile/${encodeURIComponent(String(profileId))}`
            : window.location.pathname;
        const profileUrl = `${window.location.origin}${pathname}`;
        const copied = await copyTextToClipboard(profileUrl);

        if (copied) {
            toast.success("Profile link copied successfully");
        } else {
            toast.error("Unable to copy profile link.");
        }
    };

    return (
        <MotionBox
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            sx={CARD_SX}
        >
            <Box sx={IDENTITY_CARD_SX}>
            <Box
                sx={{
                    position: "relative",
                    width: "7.5rem",
                    height: "7.5rem",
                    borderRadius: "50%",
                    overflow: "visible",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    background: `
                        radial-gradient(
                            circle at top,
                            rgba(246,185,76,0.16),
                            rgba(255,255,255,0.03)
                        )
                    `,
                    border: "1px solid rgba(255,255,255,0.08)",
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.06),
                        0 10px 24px rgba(0,0,0,0.35),
                        0 0 30px rgba(246,185,76,0.08)
                    `,
                }}
            >
                <MotionBox
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 18, ease: "linear" }}
                    sx={{
                        position: "absolute",
                        inset: "-6px",
                        borderRadius: "50%",
                        border: "1px solid rgba(246,185,76,0.22)",
                        borderTopColor: "rgba(246,185,76,0.7)",
                        borderBottomColor: "rgba(246,185,76,0.05)",
                        boxShadow: "0 0 30px rgba(246,185,76,0.12)",
                    }}
                />
                <MotionBox
                    animate={{ rotate: -360 }}
                    transition={{ repeat: Infinity, duration: 28, ease: "linear" }}
                    sx={{
                        position: "absolute",
                        inset: "8px",
                        borderRadius: "50%",
                        border: "1px dashed rgba(255,255,255,0.08)",
                    }}
                />
                <Avatar
                    src={avatarSrc}
                    alt={profileData?.nickname || profileData?.name || "Player"}
                    sx={{
                        width: "90%",
                        height: "90%",
                        border: "2px solid rgba(255,255,255,0.04)",
                        background: `
                            linear-gradient(
                                180deg,
                                rgba(255,255,255,0.03),
                                rgba(255,255,255,0.015)
                            )
                        `,
                        boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                    }}
                />
                {profileData?.flagImageUrl ? (
                    <Box
                        component="img"
                        sx={FLAG_BADGE_STYLES}
                        src={profileData.flagImageUrl}
                        alt=""
                        aria-hidden="true"
                    />
                ) : null}
                <Box
                    sx={{
                        position: "absolute",
                        inset: "9%",
                        borderRadius: "50%",
                        background: `
                            radial-gradient(
                                circle,
                                transparent 55%,
                                rgba(0,0,0,0.45) 100%
                            )
                        `,
                        pointerEvents: "none",
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0.45rem",
                    width: "100%",
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.4rem",
                        minWidth: 0,
                        maxWidth: "100%",
                    }}
                >
                    <Typography
                        title={profileData?.nickname}
                        sx={{
                            display: "block",
                            minWidth: 0,
                            color: colors.text.primary,
                            fontSize: "1.35rem",
                            textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
                            fontWeight: 600,
                            letterSpacing: "0.06em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                        }}
                    >
                        {profileData?.nickname}
                    </Typography>
                </Box>
                {headerMembership ? (
                    <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.55rem", minHeight: "1.85rem", mt: "0.15rem" }}>
                        <MembershipBadge label={headerMembership.label} size="compact" />
                    </Box>
                ) : null}
            </Box>
            </Box>

            {isOwnProfile ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "stretch",
                        gap: "0.5rem",
                        width: PROFILE_CONTENT_WIDTH,
                        mt: "0.15rem",
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                        <ProfileDesktopActionButton
                            label="Edit profile"
                            onClick={onEditProfile}
                            tone="primary"
                            fullWidth
                            startIcon={
                                <EditOutlined sx={{ fontSize: "1rem !important" }} />
                            }
                        />
                    </Box>
                    <Box
                        sx={{
                            width: "38%",
                            flexShrink: 0,
                            display: "flex",
                        }}
                    >
                        <ProfileDesktopActionButton
                            label="Share"
                            onClick={handleShareProfile}
                            fullWidth
                            startIcon={
                                <ShareOutlined sx={{ fontSize: "1rem !important" }} />
                            }
                        />
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        width: PROFILE_CONTENT_WIDTH,
                        mt: "0.15rem",
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                    {isPendingIncoming ? (
                        <Box sx={{ display: "flex", gap: "0.5rem", width: "100%" }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <ProfileDesktopActionButton
                                    label="Accept"
                                    onClick={onAcceptFriend}
                                    tone="success"
                                    fullWidth
                                    startIcon={
                                        <Check sx={{ fontSize: "1rem !important" }} />
                                    }
                                />
                            </Box>
                            <ProfileFriendIconAction
                                ariaLabel="Decline friend request"
                                tooltip="Decline request"
                                onClick={onDeclineFriend}
                            />
                        </Box>
                    ) : isPendingOutgoing ? (
                        <Box sx={{ display: "flex", gap: "0.5rem", width: "100%" }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <ProfileDesktopActionButton
                                    label="Request sent"
                                    disabled
                                    tone="pending"
                                    fullWidth
                                    startIcon={
                                        <ScheduleSendOutlined
                                            sx={{ fontSize: "0.95rem !important" }}
                                        />
                                    }
                                />
                            </Box>
                            <ProfileFriendIconAction
                                ariaLabel="Cancel friend request"
                                tooltip="Cancel request"
                                onClick={onCancelFriendRequest}
                            />
                        </Box>
                    ) : (
                        <ProfileDesktopActionButton
                            label={isFriends ? "Friends" : "Add friend"}
                            onClick={onAddFriend}
                            disabled={isFriends}
                            tone={isFriends ? "success" : "primary"}
                            fullWidth
                            startIcon={
                                isFriends ? (
                                    <HowToRegOutlined
                                        sx={{ fontSize: "1rem !important" }}
                                    />
                                ) : (
                                    <PersonAddAlt1
                                        sx={{ fontSize: "1rem !important" }}
                                    />
                                )
                            }
                        />
                    )}
                    </Box>
                    <ProfileReportIconAction onClick={onReport} />
                </Box>
            )}

            <ProfileCardDivider />

            <ProfileMetaChips
                flagImageUrl={profileData?.flagImageUrl}
                countryDisplayName={profileData?.countryDisplayName}
                joinedDateDisplay={joinedDateDisplay}
                steamProfileUrl={externalLinks.steamProfileUrl}
                faceitProfileUrl={externalLinks.faceitProfileUrl}
            />
        </MotionBox>
    );
}

ProfileHeader.propTypes = {
    profileData: PropTypes.object,
    viewerUser: PropTypes.object,
    isOwnProfile: PropTypes.bool,
    isLoading: PropTypes.bool,
    friendshipStatus: PropTypes.string,
    onAddFriend: PropTypes.func,
    onAcceptFriend: PropTypes.func,
    onDeclineFriend: PropTypes.func,
    onCancelFriendRequest: PropTypes.func,
    onReport: PropTypes.func,
    onEditProfile: PropTypes.func,
};
